import { useEffect, useMemo } from 'react';
import { SITE } from '../config/site';

type MetaProps = {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  locale: 'tr' | 'en';
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>[];
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: Record<string, unknown>[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data.length === 1 ? data[0] : data);
}

/**
 * Client-side document head manager for SPA routes.
 * Static equivalents also live in public/index.html for first paint / non-JS crawlers.
 */
export default function Seo({
  title,
  description,
  keywords,
  canonicalPath = '/',
  locale,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: MetaProps) {
  const jsonLdKey = useMemo(() => JSON.stringify(jsonLd ?? null), [jsonLd]);

  useEffect(() => {
    const canonical = `${SITE.url}${canonicalPath === '/' ? '/' : canonicalPath}`;
    const ogLocale = locale === 'tr' ? 'tr_TR' : 'en_US';
    const altLocale = locale === 'tr' ? 'en_US' : 'tr_TR';

    document.title = title;
    document.documentElement.lang = locale;

    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );
    upsertMeta('name', 'googlebot', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('name', 'author', SITE.name);
    upsertMeta('name', 'theme-color', '#1c1420');

    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:site_name', SITE.name);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:locale', ogLocale);
    upsertMeta('property', 'og:locale:alternate', altLocale);
    upsertMeta('property', 'og:image', SITE.ogImage);
    upsertMeta('property', 'og:image:alt', title);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', SITE.ogImage);
    upsertMeta('name', 'twitter:image:alt', title);

    upsertLink('canonical', canonical);
    upsertLink('alternate', canonical, 'x-default');
    upsertLink('alternate', canonical, 'tr');
    upsertLink('alternate', canonical, 'en');

    if (jsonLd?.length) {
      upsertJsonLd('loopcast-jsonld', jsonLd);
    }
  }, [title, description, keywords, canonicalPath, locale, ogType, noindex, jsonLd, jsonLdKey]);

  return null;
}
